import { useState, type ReactNode } from 'react';
import { errorMessage } from '../../api/client';
import type { WaScope } from '../../api/types';
import { useWaCreateTemplate, useWaDeleteTemplate, useWaSyncTemplates } from '../../api/whatsapp';
import type { TemplateButtonInput, TemplateCategory, WhatsappTemplate } from '../../api/types';
import { Alert, Badge, Button, EmptyState, SelectField, TextField, formatDate } from '../ui';
import { DataTable } from '../table/DataTable';
import { TEMPLATE_STATUS_COPY, WhatsAppPreview } from './shared';

const DEFAULT_FOOTER = 'Reply STOP to opt out';
const DEFAULT_BUTTON = 'Stop alerts';

/**
 * Build a template, map its `{{n}}` variables to sample values, submit it to
 * Meta, and watch the approval come back. Marketing templates carry the STOP
 * footer and a "Stop alerts" quick reply unless the author wrote their own.
 */
export function TemplateBuilder({
  templates,
  scope = {},
  canManage,
  onChanged,
}: {
  templates: WhatsappTemplate[] | null;
  scope?: WaScope;
  canManage: boolean;
  onChanged: (notice: ReactNode, kind?: 'success' | 'info' | 'error') => void;
}) {
  const sync = useWaSyncTemplates(scope);
  const syncFromMeta = () =>
    sync.mutate(undefined, {
      onSuccess: (result) =>
        onChanged(
          <>
            Synced {result.synced} template{result.synced === 1 ? '' : 's'} from Meta
            {result.inserted ? `, ${result.inserted} new` : ''}.
          </>,
          'info',
        ),
    });

  return (
    <div className="stack">
      {canManage ? <TemplateForm scope={scope} onCreated={onChanged} /> : null}
      <div className="form__row form__row--between">
        <h2>Templates</h2>
        {canManage ? (
          <Button
            variant="secondary"
            className="btn--small"
            loading={sync.isPending}
            onClick={syncFromMeta}
          >
            Sync from Meta
          </Button>
        ) : null}
      </div>
      {sync.error ? <Alert kind="error">{errorMessage(sync.error)}</Alert> : null}
      {templates && templates.length === 0 ? (
        <EmptyState title="No templates yet">
          Every WhatsApp message outside a 24-hour conversation starts from an approved template.
          Build the first one above.
        </EmptyState>
      ) : null}
      {templates && templates.length > 0 ? (
        <DataTable
          label="Templates"
          columns={[
            { key: 'name', header: 'Name' },
            { key: 'category', header: 'Category' },
            { key: 'status', header: 'Status' },
            { key: 'variables', header: 'Variables', align: 'num' },
            { key: 'opt_out', header: 'Opt-out' },
            { key: 'synced', header: 'Synced' },
            { key: 'actions', header: 'Actions', srOnly: true },
          ]}
        >
          {templates.map((t) => (
            <TemplateRow
              key={t.id}
              template={t}
              scope={scope}
              canManage={canManage}
              onChanged={onChanged}
            />
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}

function TemplateRow({
  template,
  scope,
  canManage,
  onChanged,
}: {
  template: WhatsappTemplate;
  scope: WaScope;
  canManage: boolean;
  onChanged: (notice: ReactNode, kind?: 'success' | 'info' | 'error') => void;
}) {
  const [open, setOpen] = useState(false);
  const copy = TEMPLATE_STATUS_COPY[template.status];
  const remove = useWaDeleteTemplate(scope);
  const confirmDelete = () => {
    if (!window.confirm(`Delete template "${template.name}" at Meta and here?`)) return;
    remove.mutate(template.id, {
      onSuccess: () =>
        onChanged(
          <>
            Deleted <strong>{template.name}</strong>.
          </>,
        ),
    });
  };
  return (
    <>
      <tr>
        <td>
          <code>{template.name}</code> <span className="muted">{template.language}</span>
        </td>
        <td>{template.category.toLowerCase()}</td>
        <td>
          <Badge tone={copy.tone}>{copy.label}</Badge>
        </td>
        <td className="num">{template.variableCount}</td>
        <td>
          {template.hasOptout ? <Badge tone="ok">Yes</Badge> : <Badge tone="warn">None</Badge>}
        </td>
        <td>{formatDate(template.lastSyncedAt)}</td>
        <td className="table__actions">
          <Button variant="secondary" className="btn--small" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Preview'}
          </Button>
          {canManage ? (
            <Button
              variant="danger"
              className="btn--small"
              loading={remove.isPending}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          ) : null}
        </td>
      </tr>
      {remove.error || template.rejectionReason ? (
        <tr className="table__detail">
          <td colSpan={7}>
            {remove.error ? <Alert kind="error">{errorMessage(remove.error)}</Alert> : null}
            {template.rejectionReason ? (
              <span className="muted">Meta's reason: {template.rejectionReason}</span>
            ) : null}
          </td>
        </tr>
      ) : null}
      {open ? (
        <tr className="table__detail">
          <td colSpan={7}>
            <WhatsAppPreview template={template} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function TemplateForm({
  scope,
  onCreated,
}: {
  scope: WaScope;
  onCreated: (notice: ReactNode, kind?: 'success' | 'info' | 'error') => void;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('MARKETING');
  const [language, setLanguage] = useState('en_US');
  const [headerType, setHeaderType] = useState<'NONE' | 'TEXT' | 'IMAGE'>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerHandle, setHeaderHandle] = useState('');
  const [body, setBody] = useState('');
  const [samples, setSamples] = useState<string[]>([]);
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState<TemplateButtonInput[]>([]);
  const [addOptOut, setAddOptOut] = useState(true);
  const create = useWaCreateTemplate(scope);

  const variableCount = countVariables(body);
  const effectiveFooter =
    footer.trim() || (addOptOut && category === 'MARKETING' ? DEFAULT_FOOTER : '');
  const previewButtons = [
    ...buttons.map((b) => b.text),
    ...(addOptOut &&
    category === 'MARKETING' &&
    !buttons.some((b) => /stop|opt.?out|unsubscribe/i.test(b.text))
      ? [DEFAULT_BUTTON]
      : []),
  ];

  const submit = () =>
    create.mutate(
      {
        name: name.trim(),
        language,
        category,
        headerType,
        ...(headerType === 'TEXT' ? { headerText: headerText.trim() } : {}),
        ...(headerType === 'IMAGE' ? { headerHandle: headerHandle.trim() } : {}),
        body: body.trim(),
        bodySamples: samples.slice(0, variableCount),
        ...(footer.trim() ? { footer: footer.trim() } : {}),
        buttons,
        addOptOut,
      },
      {
        onSuccess: (created) => {
          setName('');
          setBody('');
          setSamples([]);
          setFooter('');
          setButtons([]);
          onCreated(
            <>
              Submitted <strong>{created.name}</strong> to Meta; status{' '}
              {TEMPLATE_STATUS_COPY[created.status].label.toLowerCase()}.
            </>,
          );
        },
      },
    );

  return (
    <form
      className="form"
      aria-label="New template"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      {create.error ? <Alert kind="error">{errorMessage(create.error)}</Alert> : null}
      <div className="solution">
        <div className="stack">
          <div className="form form--grid">
            <TextField
              label="Template name"
              name="templateName"
              placeholder="inventory_update_v1"
              required
              value={name}
              hint="Lowercase letters, digits and underscores."
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            />
            <SelectField
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value as TemplateCategory)}
            >
              <option value="MARKETING">Marketing (offers, new inventory)</option>
              <option value="UTILITY">Utility (order and account updates)</option>
              <option value="AUTHENTICATION">Authentication (one-time codes)</option>
            </SelectField>
            <SelectField
              label="Language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="en_US">English (US)</option>
              <option value="en_GB">English (UK)</option>
              <option value="es">Spanish</option>
              <option value="es_MX">Spanish (Mexico)</option>
              <option value="pt_BR">Portuguese (Brazil)</option>
              <option value="hi">Hindi</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </SelectField>
            <SelectField
              label="Header"
              value={headerType}
              onChange={(e) => setHeaderType(e.target.value as 'NONE' | 'TEXT' | 'IMAGE')}
            >
              <option value="NONE">None</option>
              <option value="TEXT">Text</option>
              <option value="IMAGE">Image (needs a Meta media handle)</option>
            </SelectField>
            {headerType === 'TEXT' ? (
              <TextField
                label="Header text"
                name="headerText"
                maxLength={60}
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
              />
            ) : null}
            {headerType === 'IMAGE' ? (
              <TextField
                label="Media handle"
                name="headerHandle"
                value={headerHandle}
                hint="From Meta's resumable upload API."
                onChange={(e) => setHeaderHandle(e.target.value)}
              />
            ) : null}
          </div>
          <div className="field">
            <label className="field__label" htmlFor="templateBody">
              Body
            </label>
            <textarea
              id="templateBody"
              className="field__input field__textarea"
              required
              maxLength={1024}
              value={body}
              placeholder={'Hi {{1}}, new arrivals just landed at {{2}}. Reply STOP to opt out.'}
              onChange={(e) => setBody(e.target.value)}
            />
            <p className="field__hint">
              Use {'{{1}}'}, {'{{2}}'}… for values that change per message. Meta reviews the
              template with the samples below.
            </p>
          </div>
          {variableCount > 0 ? (
            <div className="form form--grid" aria-label="Variable samples">
              {Array.from({ length: variableCount }, (_, i) => (
                <TextField
                  key={i}
                  label={`Sample for {{${i + 1}}}`}
                  name={`sample${i + 1}`}
                  value={samples[i] ?? ''}
                  onChange={(e) => {
                    const next = [...samples];
                    next[i] = e.target.value;
                    setSamples(next);
                  }}
                />
              ))}
            </div>
          ) : null}
          <TextField
            label="Footer (optional)"
            name="footer"
            maxLength={60}
            value={footer}
            placeholder={category === 'MARKETING' ? DEFAULT_FOOTER : ''}
            onChange={(e) => setFooter(e.target.value)}
          />
          <ButtonsEditor buttons={buttons} onChange={setButtons} />
          <label className="field field--check">
            <input
              type="checkbox"
              checked={addOptOut}
              onChange={(e) => setAddOptOut(e.target.checked)}
            />{' '}
            Add the opt-out footer and a “{DEFAULT_BUTTON}” button when missing (required for
            marketing)
          </label>
          <div>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={!name.trim() || !body.trim()}
            >
              Submit to Meta for approval
            </Button>
          </div>
        </div>
        <div className="stack">
          <p className="muted">Preview</p>
          <WhatsAppPreview
            body={body || 'Your message will appear here.'}
            header={
              headerType === 'TEXT' ? headerText || null : headerType === 'IMAGE' ? '[IMAGE]' : null
            }
            footer={effectiveFooter || null}
            buttons={previewButtons}
            variables={samples}
          />
        </div>
      </div>
    </form>
  );
}

function ButtonsEditor({
  buttons,
  onChange,
}: {
  buttons: TemplateButtonInput[];
  onChange: (next: TemplateButtonInput[]) => void;
}) {
  const update = (i: number, patch: Partial<TemplateButtonInput>) =>
    onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className="stack">
      {buttons.map((b, i) => (
        <div key={i} className="form form--inline" aria-label={`Button ${i + 1}`}>
          <SelectField
            label="Button type"
            value={b.type}
            onChange={(e) => update(i, { type: e.target.value as TemplateButtonInput['type'] })}
          >
            <option value="QUICK_REPLY">Quick reply</option>
            <option value="URL">Link</option>
            <option value="PHONE_NUMBER">Call</option>
          </SelectField>
          <TextField
            label="Button text"
            name={`buttonText${i}`}
            maxLength={25}
            value={b.text}
            onChange={(e) => update(i, { text: e.target.value })}
          />
          {b.type === 'URL' ? (
            <TextField
              label="URL"
              name={`buttonUrl${i}`}
              value={b.url ?? ''}
              onChange={(e) => update(i, { url: e.target.value })}
            />
          ) : null}
          {b.type === 'PHONE_NUMBER' ? (
            <TextField
              label="Phone (E.164)"
              name={`buttonPhone${i}`}
              value={b.phoneNumber ?? ''}
              onChange={(e) => update(i, { phoneNumber: e.target.value })}
            />
          ) : null}
          <Button
            variant="ghost"
            className="btn--small"
            onClick={() => onChange(buttons.filter((_, j) => j !== i))}
          >
            Remove
          </Button>
        </div>
      ))}
      {buttons.length < 3 ? (
        <div>
          <Button
            variant="secondary"
            className="btn--small"
            onClick={() => onChange([...buttons, { type: 'QUICK_REPLY', text: '' }])}
          >
            Add a button
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function countVariables(body: string): number {
  const found = new Set<number>();
  for (const m of body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) found.add(Number(m[1]));
  return found.size ? Math.max(...found) : 0;
}
